/**
 * taskApiService – focused API helpers for the ChatWorkspace.
 *
 * Thin wrappers around `client.ts` that group the calls needed for the
 * unified chat/task workspace: sending messages, launching tasks, fetching
 * task details, approvals, and follow-ups.
 */
import * as client from './client';
import type { AgentStep, Artifact, ApprovalRequest, TaskFollowUp } from '../types';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface TaskDetails {
  taskId: string;
  goal: string;
  status: string;
  steps: AgentStep[];
  artifacts: Artifact[];
  approvals: ApprovalRequest[];
  followUps: TaskFollowUp[];
  finalAnswer: string;
  createdAt: string;
  completedAt: string;
}

export interface SendChatOptions {
  sessionId: string;
  kbIds?: string[];
  kbSearchLimit?: number;
}

export interface LaunchTaskOptions {
  userId: string;
  sessionId: string;
  chatHistory: client.ChatMessage[];
  kbIds?: string[];
  agentType?: string;
}

const TASK_AUTH_ERROR_MESSAGE =
  'Task service authentication failed. Check that Jenkins exports ASSISTANT_API_KEY and that the public /api/task-service proxy injects the matching X-API-Key header. Do not expose the key in React.';

export function describeTaskApiError(error: any): string {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  if (status === 401) {
    return TASK_AUTH_ERROR_MESSAGE;
  }
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (error?.message) {
    return error.message;
  }
  return 'unknown';
}

// ── Chat ──────────────────────────────────────────────────────────────────────

/**
 * Send a chat message (with optional KB context injection) and return the
 * assistant reply string.
 */
export async function sendChatMessage(
  messages: client.ChatMessage[],
  options: SendChatOptions,
): Promise<string> {
  const { sessionId, kbIds = [], kbSearchLimit = 6 } = options;

  let contextPrefix = '';
  if (kbIds.length > 0) {
    try {
      const lastUserContent = [...messages].reverse().find(m => m.role === 'user')?.content ?? '';
      contextPrefix = await client.buildSelectedKnowledgeBasePromptContext(
        kbIds,
        lastUserContent,
        kbSearchLimit,
      );
    } catch {
      // KB search failed — proceed without context
    }
  }

  const messagesWithContext: client.ChatMessage[] = contextPrefix
    ? [
        ...messages.slice(0, -1),
        { role: 'user', content: contextPrefix + (messages[messages.length - 1]?.content ?? '') },
      ]
    : messages;

  const res = await client.chatWithLLM(messagesWithContext, { sessionId });
  return res.data?.reply ?? '';
}

// ── Tasks ─────────────────────────────────────────────────────────────────────

/**
 * Execute a new autonomous task, passing the current chat session as context
 * so the task agent has conversation history available.
 */
export async function launchTaskFromChat(
  goal: string,
  options: LaunchTaskOptions,
): Promise<{ id: string; status: string }> {
  const { userId, sessionId, chatHistory, kbIds = [], agentType = 'autonomous' } = options;
  const inputContext: Record<string, any> = {
    source_view: 'chat',
    chat_session_id: sessionId,
    chat_history: chatHistory,
  };

  try {
    const res = await client.executeTask(
      goal,
      agentType,
      '',
      userId,
      '',
      inputContext,
      kbIds,
      '',
      'standard',
    );
    return res.data;
  } catch (error: any) {
    throw new Error(describeTaskApiError(error));
  }
}

/**
 * Fetch task metadata, execution steps, artifacts, pending approvals, and
 * follow-ups in parallel and return a unified detail object.
 */
export async function fetchTaskDetails(taskId: string): Promise<TaskDetails> {
  const [taskRes, stepsRes, artifactsRes, approvalsRes, followUpsRes] = await Promise.allSettled([
    client.getTask(taskId),
    client.getTaskSteps(taskId),
    client.getTaskArtifacts(taskId),
    client.listApprovals({ task_id: taskId }),
    client.getTaskFollowUps(taskId, { limit: 20 }),
  ]);

  const raw = taskRes.status === 'fulfilled' ? taskRes.value.data : {};
  const steps: AgentStep[] =
    stepsRes.status === 'fulfilled'
      ? (stepsRes.value.data?.steps ?? stepsRes.value.data ?? [])
      : [];
  const artifacts: Artifact[] =
    artifactsRes.status === 'fulfilled'
      ? (artifactsRes.value.data?.artifacts ?? artifactsRes.value.data ?? [])
      : [];
  const approvals: ApprovalRequest[] =
    approvalsRes.status === 'fulfilled'
      ? (approvalsRes.value.data?.approvals ?? approvalsRes.value.data ?? [])
      : [];
  const followUps: TaskFollowUp[] =
    followUpsRes.status === 'fulfilled'
      ? (followUpsRes.value.data?.follow_ups ?? followUpsRes.value.data ?? [])
      : [];

  return {
    taskId,
    goal: raw?.goal ?? '',
    status: raw?.status ?? '',
    steps,
    artifacts,
    approvals,
    followUps,
    finalAnswer: raw?.final_answer ?? '',
    createdAt: raw?.created_at ?? '',
    completedAt: raw?.completed_at ?? '',
  };
}

// ── Approvals ─────────────────────────────────────────────────────────────────

export const approveTaskRequest = (approvalId: string, note = '') =>
  client.approveRequest(approvalId, note);

export const rejectTaskRequest = (approvalId: string, note = '') =>
  client.rejectRequest(approvalId, note);

// ── Follow-ups ────────────────────────────────────────────────────────────────

export const submitTaskFollowUp = (taskId: string, message: string) =>
  client.createTaskFollowUp(taskId, message);

// ── URL-hash session routing ──────────────────────────────────────────────────

const SESSION_HASH_PREFIX = '#chat/';

/** Read the active session ID from window.location.hash. */
export function readSessionFromHash(): string {
  const hash = window.location.hash;
  if (hash.startsWith(SESSION_HASH_PREFIX)) {
    return hash.slice(SESSION_HASH_PREFIX.length).split('/')[0];
  }
  return '';
}

/** Write a session ID into window.location.hash without triggering a page reload. */
export function writeSessionToHash(sessionId: string): void {
  const target = sessionId ? `${SESSION_HASH_PREFIX}${sessionId}` : '';
  if (window.location.hash !== target) {
    window.history.replaceState(null, '', target || window.location.pathname + window.location.search);
  }
}
